import { Severity } from 'Data/ViewModels/Core/types';
import { Eye, EyeOff } from 'Lib/Icons';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import coalesce from '../../../Lib/coalesce';
import { withInjectedProps } from '../InjectedPropsContext';
import './PasswordInput.scss';

export type IPasswordInputProps = Pick<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  | 'autoFocus'
  | 'defaultValue'
  | 'disabled'
  | 'id'
  | 'maxLength'
  | 'name'
  | 'onChange'
  | 'onKeyDown'
  | 'placeholder'
  | 'onBlur'
> & {
  focusRef?: React.Ref<HTMLInputElement>;
  password?: boolean;
  hideVisibilityEye?: boolean;
  severity?: Severity;
  value?: any;
  onShowPassword?: (isHidingPassword: boolean) => void;
};

const PasswordInputBase: React.FC<IPasswordInputProps> = ({
  autoFocus,
  defaultValue,
  disabled,
  id,
  focusRef,
  maxLength,
  name,
  onChange,
  onKeyDown,
  hideVisibilityEye = false,
  placeholder,
  severity,
  value,
  onBlur,
  onShowPassword,
}) => {
  const [hidePassword, setHidePassword] = useState(true);
  const [showVisibilityEye, setShowVisibilityEye] = useState(false);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    (event as any).data = { name, value: event.target.value, submitValues: false };
    onChange?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onChange) {
      (event as any).data = { name, value: event.target.value.trim(), submitValues: true };
      onChange(event as any);
    }
    onBlur?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.defaultPrevented) {
      (event as any).data = { name, value: (event.target as HTMLInputElement).value.trim(), submitValues: true };
      onChange?.(event as any);
    }
    onKeyDown?.(event);
  };
  const handlePasswordToggle = (e: any) => {
    e.preventDefault();
    setHidePassword(!hidePassword);
    onShowPassword?.(hidePassword);
  };

  const cx = classNames('PasswordInput', { [`Input-${severity}`]: !!severity });
  //Only show PasswordVisibility Eye if hideVisibilityEye is not set to true and there is password value
  useEffect(() => {
    setShowVisibilityEye(!hideVisibilityEye && value);
  }, [hideVisibilityEye, value]);
  return (
    <div className="PasswordInputField">
      <span className="PasswordInput-Text">
        <input
          autoFocus={autoFocus}
          className={cx}
          defaultValue={defaultValue === undefined ? defaultValue : coalesce(defaultValue, '')}
          disabled={disabled}
          id={id}
          maxLength={maxLength}
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? `Enter password`}
          ref={focusRef}
          type={hidePassword ? 'password' : 'text'}
          value={value === undefined ? value : coalesce(value, '')}
        />
      </span>
      {showVisibilityEye && (
        <span className="PasswordInput-PasswordIcon" onClick={handlePasswordToggle}>
          {hidePassword ? <Eye size="1.5em" /> : <EyeOff size="1.5em" />}
        </span>
      )}
    </div>
  );
};

const PasswordInput = withInjectedProps(PasswordInputBase);

export { PasswordInput, PasswordInputBase };
export default PasswordInput;
